import { type AgentDefinition, HEAVY_BUDGET, MEDIUM_BUDGET, TOOLS } from './types.js';
import { agentPrompt } from './agent-prompts.js';

/** Phase 6 · Domain — specialists for the major slices of a system. */
export const DOMAIN_AGENTS: AgentDefinition[] = [
  {
    config: {
      id: 'database',
      name: 'Database',
      role: 'database',
      tools: [...TOOLS.build],
      prompt: agentPrompt('database'),
    },
    budget: HEAVY_BUDGET,
    capability: {
      phase: 'domain',
      summary: 'Schema design, query optimization, and safe reversible migrations for SQL databases.',
      keywords: [
        'database',
        'schema',
        'sql',
        'migration',
        'query',
        'index',
        'postgres',
        'mysql',
        'table',
        'orm',
        'slow query',
      ],
    },
  },
  {
    config: {
      id: 'api',
      name: 'API',
      role: 'api',
      tools: [...TOOLS.build, 'fetch'],
      prompt: agentPrompt('api'),
    },
    budget: HEAVY_BUDGET,
    capability: {
      phase: 'domain',
      summary: 'REST + GraphQL API design and implementation: contracts, HTTP/GraphQL semantics, versioning.',
      keywords: [
        'api',
        'rest',
        'graphql',
        'endpoint',
        'resolver',
        'http',
        'openapi',
        'swagger',
        'route',
        'contract',
        'webhook',
      ],
    },
  },
  {
    config: {
      id: 'auth',
      name: 'Auth',
      role: 'auth',
      tools: [...TOOLS.build],
      prompt: agentPrompt('auth'),
    },
    budget: HEAVY_BUDGET,
    capability: {
      phase: 'domain',
      summary: 'Authentication and authorization: identity, sessions/tokens, RBAC/ABAC, OAuth/OIDC, done securely.',
      keywords: [
        'auth',
        'authentication',
        'authorization',
        'login',
        'session',
        'jwt',
        'oauth',
        'oidc',
        'rbac',
        'permissions',
        'token',
        'sso',
      ],
    },
  },
  {
    config: {
      id: 'data',
      name: 'Data',
      role: 'data',
      tools: [...TOOLS.build],
      prompt: agentPrompt('data'),
    },
    budget: HEAVY_BUDGET,
    capability: {
      phase: 'domain',
      summary: 'Data engineering: ETL/ELT pipelines, data-quality validation, idempotent transforms, reconciliation.',
      keywords: [
        'etl',
        'elt',
        'pipeline',
        'data quality',
        'data engineering',
        'transform',
        'ingestion',
        'batch',
        'stream',
        'reconcile',
        'dataset',
      ],
    },
  },
  {
    config: {
      id: 'frontend',
      name: 'Frontend',
      role: 'frontend',
      tools: [...TOOLS.build, 'fetch'],
      prompt: agentPrompt('frontend'),
    },
    budget: HEAVY_BUDGET,
    capability: {
      phase: 'domain',
      summary: 'UI implementation: components, client state, data fetching, responsive and accessible by default.',
      keywords: [
        'frontend',
        'component',
        'react',
        'vue',
        'svelte',
        'client state',
        'ui implementation',
        'css',
        'responsive',
        'hook',
        'render',
      ],
    },
  },
  {
    config: {
      id: 'backend',
      name: 'Backend',
      role: 'backend',
      tools: [...TOOLS.build],
      prompt: agentPrompt('backend'),
    },
    budget: HEAVY_BUDGET,
    capability: {
      phase: 'domain',
      summary: 'Server-side logic: services, business rules, persistence/queue wiring, concurrency and transactions.',
      keywords: [
        'backend',
        'server',
        'service',
        'business logic',
        'controller',
        'handler',
        'queue',
        'cache',
        'transaction',
        'microservice',
        'server-side',
      ],
    },
  },
  {
    config: {
      id: 'designer',
      name: 'Designer',
      role: 'designer',
      tools: [...TOOLS.docs],
      prompt: agentPrompt('designer'),
    },
    budget: MEDIUM_BUDGET,
    capability: {
      phase: 'domain',
      summary: 'UI/UX design: user flows, layout/wireframes, interaction states, and design-system decisions.',
      keywords: [
        'design',
        'ux',
        'ui design',
        'wireframe',
        'user flow',
        'layout',
        'design system',
        'interaction',
        'mockup design',
        'information architecture',
      ],
    },
  },
];
